import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_URL } from '../config/api';
import { loadPermissions } from '../utils/permissions';

const ClientLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Handle Microsoft OAuth callback
  useEffect(() => {
    const token = searchParams.get('token');
    const userParam = searchParams.get('user');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      const errorMessages = {
        microsoft_denied: 'Microsoft login was cancelled.',
        not_invited: 'Your Microsoft account is not registered. Contact your account manager.',
        token_failed: 'Microsoft authentication failed. Please try again.',
        no_email: 'Could not retrieve email from Microsoft account.',
        server_error: 'Server error during Microsoft login. Please try again.',
      };
      setError(errorMessages[errorParam] || 'Login failed. Please try again.');
      window.history.replaceState({}, '', '/client/login');
      return;
    }

    if (token && userParam) {
      try {
        const user = JSON.parse(decodeURIComponent(userParam));
        localStorage.setItem('authToken', token);
        localStorage.setItem('user', JSON.stringify(user));
        loadPermissions();
        window.history.replaceState({}, '', '/client/dashboard');
        navigate('/client/dashboard');
      } catch (err) {
        console.error('Error parsing Microsoft callback:', err);
        setError('Login failed. Please try again.');
        window.history.replaceState({}, '', '/client/login');
      }
    }
  }, [searchParams, navigate]);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) { setError('Email is required'); return; }
    if (!validateEmail(email)) { setError('Please enter a valid email address'); return; }
    if (!password) { setError('Password is required'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Invalid credentials');

      if (data.user.user_type !== 'client') {
        setError('This portal is for clients only. Please use the recruiter portal.');
        setLoading(false);
        return;
      }

      localStorage.setItem('authToken', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      await loadPermissions();
      navigate('/client/dashboard');
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftLogin = () => {
    window.location.href = `${API_URL}/auth/microsoft?from=client`;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="px-6 py-4 flex items-center justify-between max-w-6xl mx-auto w-full">
        <a href="/" className="flex items-center gap-2.5">
          <img src="/logo.svg" alt="Banyak Group" className="h-8 w-8" />
          <span className="text-lg font-bold text-gray-900 tracking-tight">Platform</span>
        </a>
        <button onClick={() => navigate('/login')}
          className="px-5 py-2 text-sm font-medium bg-emerald-800 text-white rounded-lg hover:bg-emerald-900 transition-colors">
          Recruiter Sign In
        </button>
      </header>

      <div className="flex-1 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
        <div className="flex items-center justify-center mb-2">
          <img src="/Logo-Full-Colored.svg" alt="Banyak Group" className="h-14" />
        </div>
        <p className="text-center text-xs text-emerald-700 font-medium mb-6">Client Portal</p>
        <p className="text-center text-gray-600 mb-8">Sign in to continue</p>

        <button
          type="button"
          onClick={handleMicrosoftLogin}
          className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors mb-6"
        >
          <svg width="20" height="20" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
            <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
            <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
            <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
            <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
          </svg>
          <span className="text-gray-700 font-medium">Sign in with Microsoft</span>
        </button>

        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-3 bg-white text-gray-400">or</span>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="you@company.com" disabled={loading} autoFocus required />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="Enter password" disabled={loading} required />
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <button type="submit" disabled={loading || !email || !password}
            className="w-full px-4 py-2 bg-emerald-800 text-white rounded-lg hover:bg-emerald-900 disabled:opacity-50 disabled:cursor-not-allowed font-medium">
            {loading ? 'Signing in...' : 'Sign In'}
          </button>

          <div className="text-center mt-4">
            <button type="button" onClick={() => navigate('/client/forgot-password')}
              className="text-sm text-emerald-700 hover:text-emerald-900 hover:underline">
              Forgot your password?
            </button>
          </div>
        </form>

        <div className="mt-8 pt-6 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-400">Need help? Contact your account manager.</p>
        </div>
      </div>
      </div>
    </div>
  );
};

export default ClientLogin;
